import { inject, Injectable } from "@angular/core";
import { StatsigService } from "@statsig/angular-bindings";
import { Experiment } from "@statsig/js-client";
import { datadogRum } from "@datadog/browser-rum";
import { LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { BehaviorSubject, filter, map, Observable, of, switchMap } from "rxjs";
import { Dictionary } from "@helpers/types";

export enum FEAT_GATES {
	//SURVEY_DEPOSIT = "broker.surveys.deposit_insights",
	// STOCKS_20_CUSTODY_ACCOUNT_OPENING = "broker.web.stocks_20.custody_account_opening_temporary", // @TODO: use the real LD flag when feature is completed
	PAYSAFE_MASTERCARD = "broker.psp.paysafe_allowed_deposit_mastercard",
	PAYSAFE_VISA_CREDIT = "broker.psp.paysafe_allowed_deposit_visa_credit_card",
	GQL_TRADE_DERIVATIVES = "trade_engine.general.device_invocation.securities_enabled",
	STAKING_ENABLED = "broker.staking",
	LEVERAGE = "broker.fe.leverage",
	TRADE_SUCCESSFUL_SCREEN_REDESIGN = "trading_flows.web.trade_success_screen_redesign",
	TRANSACTION_SHARE_ENABLED = "trading_flows.web.transaction_share_enabled",
	GQL_TRADE_BCI = "trade_engine.general.device_invocation.bci_enabled",
	DEPOSIT_NEW_DEPOSIT_FLOW_ENABLED = "broker.deposit.new_deposit_flow.enabled",
	DEPOSIT_MIGRATION_ENABLED = "broker.payments.ledger.v2.deposit.endpoints.migration.enabled",
	ADP_SHARE_ENABLED = "trading_flows.web.asset_share_enabled",
	BUY_AGAIN = "broker.fe.section.trade_again",
	PORTFOLIO_EMPTY_STATE_V3 = "portfolio-empty-state-v3",
	DARK_MODE_ENABLED = "broker.web.dark_mode_enabled",
	PORTFOLIO_GROUPED_PERFORMANCE = "portfolio.grouped_performance_kpi",
	CHECKOUT_FLOW_ENABLED = "broker.fe.checkout",
	AFFILIATES_REFERRALS_REVAMP = "affiliates.general.referrals_revamp",
	PORTFOLIO_NEW_TIME_FILTERS = "portfolio.new_chart_filters",
	PORTFOLIO_CROSS_SELLING_SECTION = "portfolio.cross_selling_section",
	NOTIFICATIONS = "broker.fe.notification_center",
	NEW_GLOBAL_NOTIFICATIONS = "broker.fe.notification_center.global_notifications",
	PORTFOLIO_HIDE_BCI_KPIS = "portfolio.hide-bci-kpis",
	CASH_PLUS = "broker.earn_on_fiat",
	LEVERAGE_CFD = "broker.fe.leverage-cfd-changes",
	DEPOSIT_MULTICHAIN = "broker.payments.cryptocoin.deposit.multichain.enabled",
	WITHDRAWAL_MULTICHAIN = "payments.cryptocoin.withdraw.multichain.enabled",
	APPROPRIATENESS_TEST_V2 = "broker.appropriateness_test.v2",
	LOCKED_STAKING = "broker.eatr.locked_staking",
	PREFERENCE_CENTRE = "martech.communications_preferences_center.enabled",
	BANKID = "cat.bankid_scrive_redirect_sweden_residents",
	NEW_FEATURE_HIGHLIGHT = "broker.fe.new-feature-highlight",
	BITPANDA_CLUB_V2 = "broker.fe.bitpanda-club-v2",
	BITPANDA_STORIES = "broker.fe.stories",
	TAX_WITHHOLDING = "broker.fe.tax_withholding",
	TAX_WITHHOLDING_TRADE = "broker.fe.trade.tax_withholding",
	TAX_WITHHOLDING_SUMMARY_VARIATION = "broker.fe.summary_variation.hide_tax_withheld_amount",
	HIDE_BEST_REWARD_STATUS = "broker.fe.hide_best_rewards_status",
	TAX_SPLIT_DEPOSIT = "broker.fe.tax_withholding.split_deposit",
	CHANGE_PHONE = "broker.fe.change_phone",
	CHANGE_EMAIL = "broker.fe.change_email",
	CHANGE_ADDRESS = "broker.fe.change_address",
	UK_FINPROM_GLOBAL_WARNING = "broker.fe.uk_fin_prom_banner.enabled",
	UK_FINPROM_GLOBAL_RISK_WARNING = "broker.fe.global_risk_warning_banner.enabled",
	HIDE_PORTFOLIO = "broker.fe.hide.portfolio.amount",
	POF_NEW_HEADER = "broker.fe.proof_of_funds_new_header",
	TW_CONTINUE_ANYAWY_LOG = "broker.fe.tax_withholding_log_continue_anyway",
	NOTIFICATION_PROMPT = "broker.fe.dashboard_notification_prompts",
	LIMIT_ORDERS_BUY = "broker.fe.buy.limit_orders",
	LIMIT_ORDERS_SELL = "broker.fe.sell.limit_orders",
	LIMIT_ORDERS_HOME_ADP = "broker.fe.limit_order_home_adp_redesign",
	HIDE_BIRTHDAY = "broker.fe.hide_birthday",
	DUST_TO_BEST = "broker.fe.dust_to_best",
	HIDE_METAL_STORAGE_FEE = "broker.fe.hide_metal_storage_fee_toggle",
	BP_CARD_LIMITED_STOCK = "broker.fe.card.order.limited_stock",
	PAYMENTS_FIAT_DEPOSIT_FREE = "payments.fiat.deposit.all.methods.free",
	ADVANCED_TRADING = "broker.web.advanced-trading",
	ASSET_LOGOS_V4 = "broker.be.v4_logos.enabled",
	SECURITY_TOKENS = "broker.fe.security-tokens-category",
	ITB_REWARDS_TOGGLE = "best-rewards-5-years-itb-toggle",
	ANTI_PHISHING_CODE = "broker.fe.antiphishing_code",
	UK_FIN_PROM_REDIRECT = "broker.uk_fin_prom.redirect",
	PAYMENTS_TRAVEL_RULE = "broker.payments.travel_rule",
	LIMIT_ORDERS_SWITCH_VARIATIONS = "amount_screen_bottom_sheet_variation_on_order_types",
	ANTI_PHISHING_BLOCK_UX_IF_UNSET = "broker.fe.antiphishing_code_block_if_unset",
	FEE_TRANSPARENCY_CRYPTO_ONLY = "frontend_fee_transparency_crypto_only",
	PAYMENTS_ADDRESS_BOOK = "broker.payments.address_book",
	PAYMENTS_FIAT_SEGREGATION_DD = "broker.payments.fiat.segregation.dd",
	TERMS_AND_CONDITIONS = "broker.fe.terms_and_conditions",
	BP_CARD_NOTIFY_CARD_AVAILABLE = "broker.fe.card.rework.notify_card_available",
	USER_REPORTS = "broker.fe.user_reports",
	AUTO_STAKING = "fe.autostaking_portfolio",
	FUSION_SELF_CERTIFICATION = "broker.web.fusion.self-certification",
	SWAP_SERVICE_GRAPHQL = "broker.fe.enable.trade_service_swap_offer",
	STATSIG_ENABLED = "broker.fe.statsig_experiments.enabled",
	UNIT_SWITCH_AMOUNT = "unit-switcher-on-amount-screen",
	MARGIN_TRADING_ENABLED = "fe-margin-trading",
	MARGIN_TRADING_BUY = "broker.fe.margin-trading-buy",
	MARGIN_TRADING_SELL = "broker.fe.margin-trading-sell",
	MARGIN_TRADING_LIMIT_ORDER_ANNOUNCEMENT = "fe-margin-trading-limit-order-announcement",
	TRAVEL_RULE_BUSINESS_FLOW = "broker.payments.travel.rule.business.flow",
	MARKET_ORDER_DEFAULT_TRADE_API_SWITCH = "broker.fe.market_order_default_switch_trade_api",
	LIMIT_ORDER_DEFAULT_SWITCH = "broker.fe.limit_order_default_switch_trade_api",
	MARGIN_TRADING_DEFAULT_SWITCH = "broker.fe.margin_trading_default_switch_trade_api",
	MARGIN_TRADING_LIMIT_ORDER = "broker.fe.margin_trading.limit_order",
	MARGIN_TRADING_HEADER_SWITCH = "broker.fe.margin_bottom_sheet_label",
	MT_LO_DEFAULT_SWITCH = "broker.fe.margin_trading_limit_order_default_switch_trade_api",
	REMOVE_STEELCOIN = "broker.fe.remove-steelcoin-from-flows",
	EARN_ON_STABLECOIN = "broker.fe.earn_on_stable_coins",
	PAYMENTS_VOP = "broker.fe.payments.vop.enabled",
	MT_APPROPRIATENESS_TEST_V2 = "broker.fe.mt-questionaire-new-flow",
	TIN_TAX_ENABLED = "broker.web.tin-collection-flow",
	REAL_SECURITIES_ENABLED = "fe-real-securities",
	SWAP_ESTIMATE_GQL_ENABLED = "fe.estimate_swap_prices_query_enabled",
	FUSION_EXPERIENCE_ENABLED = "broker.fusion.experience.enabled",
	TRAVEL_RULE_V2 = "broker.payments.travel.rule.v2",
	SHW_3RD_PARTY_DISABLED = "broker.payments.travel.rule.shw.third.party.disable"
}

export enum DYNAMIC_CONFIGS {
	WEB3_TOKEN_MERGE = "broker.fe.web3_token_merge",
	PANTOS_PID = "broker.fe.pantos_pid",
	BEST_PID = "broker.fe.best_pid",
	VISION_PID = "broker.fe.vision_pid",
	WEB3_TOKEN_MERGE_URLS = "broker.fe.web3_token_merge_urls",
	HOME_BANNER_TYPE = "broker.fe.home_banner_type",
	WEB3_VSN_FEE_BENEFIT = "broker.fe.vsn_fee_benefits",
	RS_REDIRECTION_TYPE = "fe.rs.webapp.redirection_type"
}

export enum RS_REDIRECTION_TYPE {
	OFF = "off",
	PLAN_A = "plan_a", // redirect to V4
	PLAN_B = "plan_b" // "redirect" to mobile
}

export enum EXPERIMENTS {}

@Injectable({
	providedIn: "root"
})
export class StatsigClientService {
	private statsigService: StatsigService = inject(StatsigService);
	private initService: InitService = inject(InitService);
	updatedUser = false;
	private featureGates$ = new BehaviorSubject<Dictionary<boolean>>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private dynamicConfigs$ = new BehaviorSubject<Dictionary<any>>(null);

	private isLoading$ = this.statsigService.isLoading$.pipe(
		map((isLoading) => {
			if (!isLoading && this.updatedUser) {
				this.initService.init(ServiceNames.STATSIG);
			}
			return isLoading && this.updatedUser;
		})
	);

	updateUser(userId: string, email: string, country: string): void {
		this.statsigService.getClient().getContext().user.appVersion;
		const user = {
			userID: userId,
			appVersion: this.statsigService.getClient().getContext().user.appVersion,
			country,
			custom: {
				platform: "web",
				emailDomain: email.split("@")[1]
			}
		};

		this.statsigService
			.updateUserAsync(user)
			.then(() => {
				this.updatedUser = true;
				this.updateFlags();
				this.initService.init(ServiceNames.STATSIG);
				setInterval(() => {
					this.statsigService.updateUserAsync(user).then(() => this.updateFlags());
				}, 60000);
			})
			.catch((error) => {
				console.error("Error updating user:", error);
				this.initService.init(ServiceNames.STATSIG);
			});
	}

	isFeatureActive(gateKey: string): Observable<boolean> {
		const featureGate = this.statsigService.getClient().getFeatureGate(gateKey);
		if (featureGate.details.reason.toLowerCase() === "unrecognized") {
			datadogRum.addError("Feature gate not recognized", featureGate);
			return of(null);
		}

		return this.featureGates$.asObservable().pipe(
			filter((gates) => gates !== null),
			map((gates) => gates?.[gateKey] ?? null)
		);
	}

	getDynamicConfig<TConfig>(configKey: DYNAMIC_CONFIGS | LD_FLAG): Observable<TConfig> {
		const dynamicConfig = this.statsigService.getDynamicConfig(configKey);
		if (dynamicConfig.details.reason.toLowerCase() === "unrecognized") {
			datadogRum.addError("Dynamic config not recognized", dynamicConfig);
			return of(null);
		}

		return this.dynamicConfigs$.asObservable().pipe(
			filter((configs) => configs !== null),
			map((configs) => configs?.[configKey] ?? null)
		);
	}

	getExperiment(experimentKey: string): Observable<Experiment> {
		const experiment = this.statsigService.getExperiment(experimentKey);
		if (experiment.details.reason.toLowerCase() === "unrecognized") {
			datadogRum.addError("Experiment not recognized", experiment);
			return of(null);
		}

		return this.isLoading$.pipe(
			filter((isLoading) => !isLoading),
			switchMap(() => of(experiment))
		);
	}

	private featFlags: keyof typeof FEAT_GATES;
	private dynamicConfigs: keyof typeof DYNAMIC_CONFIGS;

	private updateFlags(): void {
		const gateChanges = {};
		for (this.featFlags in FEAT_GATES) {
			const flagName = FEAT_GATES[this.featFlags];
			if (
				(this.featureGates$.value &&
					this.featureGates$.value[flagName] !==
						this.statsigService.checkGate(flagName, { disableExposureLog: true })) ||
				!this.featureGates$.value
			) {
				gateChanges[flagName] = this.statsigService.checkGate(flagName);
			}
		}
		if (Object.keys(gateChanges).length > 0) {
			this.featureGates$.next({ ...this.featureGates$.value, ...gateChanges });
		}

		const dynamicConfigChanges = {};
		for (this.dynamicConfigs in DYNAMIC_CONFIGS) {
			const flagName = DYNAMIC_CONFIGS[this.dynamicConfigs];
			if (flagName === DYNAMIC_CONFIGS.HOME_BANNER_TYPE) {
				if (
					(this.dynamicConfigs$.value &&
						this.dynamicConfigs$.value[flagName] !==
							this.statsigService.getDynamicConfig(flagName).value["value"]) ||
					!this.dynamicConfigs$.value
				) {
					dynamicConfigChanges[flagName] = this.statsigService.getDynamicConfig(flagName).value["value"];
				}
			} else {
				if (
					(this.dynamicConfigs$.value &&
						this.dynamicConfigs$.value[flagName] !==
							this.statsigService.getDynamicConfig(flagName).value) ||
					!this.dynamicConfigs$.value
				) {
					dynamicConfigChanges[flagName] = this.statsigService.getDynamicConfig(flagName).value;
				}
			}
		}

		if (Object.keys(dynamicConfigChanges).length > 0) {
			this.dynamicConfigs$.next({ ...this.dynamicConfigs$.value, ...dynamicConfigChanges });
		}
	}
}
